import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/navigation/Sidebar';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import QuickActionMenu from '../../components/navigation/QuickActionMenu';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import toast from 'react-hot-toast';
import ContractCard from './components/ContractCard';
import ContractDetailModal from './components/ContractDetailModal';
import TimeEntryForm from './components/TimeEntryForm';
import MessagingPanel from './components/MessagingPanel';
import { getUserContracts } from '../../services/contractService';
import { createConnectAccount, createAccountLink, syncConnectAccountStatus, getConnectBalance, requestPayout } from '../../services/stripeConnectService';
import { supabase } from '../../lib/supabase';

const ExpertDashboard = () => {
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate ? useNavigate() : null;
  const location = useLocation();
  const [contracts, setContracts] = useState([]);
  const [cases, setCases] = useState([]);
  const [expertProfile, setExpertProfile] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState(null);
  const [detailContract, setDetailContract] = useState(null);
  const [contractTab, setContractTab] = useState('all');
  const [connectOnboarding, setConnectOnboarding] = useState(false);
  const [financialTab, setFinancialTab] = useState('overview');
  const [connectBalance, setConnectBalance] = useState({ available: 0, pending: 0, heldInEscrow: 0, totalGross: 0 });
  const [balanceLoaded, setBalanceLoaded] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('standard');
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [averageRating, setAverageRating] = useState(null);

  // Determine active view from URL
  const getActiveView = () => {
    const path = location.pathname;
    if (path.includes('/contracts')) return 'contracts';
    // if (path.includes('/cases')) return 'cases';
    if (path.includes('/time-tracking')) return 'time-tracking';
    if (path.includes('/messages')) return 'messages';
    if (path.includes('/financial')) return 'financial';
    return 'overview';
  };
  const activeView = getActiveView();
  const msgInitUserId = new URLSearchParams(location.search).get('userId') || undefined;

  useEffect(() => {
    if (user?.id) {
      loadExpertData();
    }
  }, [user?.id]);

  // Auto-sync Connect status when Stripe redirects back after onboarding.
  // Stripe appends ?onboarding=complete (returnUrl) or ?refresh=true (refreshUrl).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isReturn  = params.get('onboarding') === 'complete';
    const isRefresh = params.get('refresh') === 'true';
    if ((isReturn || isRefresh) && user?.id) {
      // Small delay to allow loadExpertData to finish first so expertProfile is set
      const timer = setTimeout(async () => {
        try {
          // Pull the expert profile id fresh — state may not be set yet
          const { data: profile } = await supabase
            .from('expert_profiles')
            .select('id')
            .eq('user_id', user.id)
            .single();
          if (!profile?.id) return;
          const result = await syncConnectAccountStatus(profile.id);
          if (result?.success) {
            toast.success('Bank account connected successfully!');
            await loadExpertData();
            // Clean up URL params without a full reload
            navigate(location.pathname, { replace: true });
          } else {
            toast.error(result?.error || 'Could not sync Stripe status — please click "Refresh Status" manually.');
          }
        } catch (e) {
          console.error('Auto-sync Connect status error:', e);
        }
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [location.search, user?.id]);

  // Load Connect balance whenever financial tab opens (or on mount)
  useEffect(() => {
    if (expertProfile?.id && !balanceLoaded) {
      getConnectBalance(expertProfile.id).then((res) => {
        console.log('[ExpertDashboard] balance loaded (useEffect):', JSON.stringify(res));
        if (res.success) {
          const next = { available: res.available ?? 0, pending: res.pending ?? 0, heldInEscrow: res.heldInEscrow ?? 0, totalGross: res.totalGross ?? 0 };
          console.log('[ExpertDashboard] setting connectBalance:', JSON.stringify(next));
          setConnectBalance(next);
        }
        setBalanceLoaded(true);
      });
    }
  }, [expertProfile?.id, balanceLoaded]);

  const refreshBalance = async () => {
    if (!expertProfile?.id) return;
    const res = await getConnectBalance(expertProfile.id);
    console.log('[ExpertDashboard] balance refreshed:', JSON.stringify(res));
    if (res.success) {
      const next = { available: res.available ?? 0, pending: res.pending ?? 0, heldInEscrow: res.heldInEscrow ?? 0, totalGross: res.totalGross ?? 0 };
      console.log('[ExpertDashboard] setting connectBalance (refresh):', JSON.stringify(next));
      setConnectBalance(next);
    }
    setBalanceLoaded(true);
  };

  const handleRequestPayout = async () => {
    const amt = parseFloat(payoutAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    const maxWithdrawable = connectBalance.available + connectBalance.pending;
    if (amt > maxWithdrawable) { toast.error(`Maximum available: $${maxWithdrawable.toFixed(2)}`); return; }
    setRequestingPayout(true);
    try {
      const res = await requestPayout(expertProfile.id, amt, payoutMethod);
      if (!res.success) {
        if (res.onboardingRequired) {
          toast.error('Please complete KYC verification first');
          // requestPayout auto-redirects to onboarding
          return;
        }
        throw new Error(res.error);
      }
      const eta = res.estimatedArrival
        ? new Date(res.estimatedArrival).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : payoutMethod === 'instant' ? '~30 minutes' : '2–3 business days';
      toast.success(`Payout initiated — estimated arrival: ${eta}`);
      setShowPayoutModal(false);
      setPayoutAmount('');
      setBalanceLoaded(false);
      await refreshBalance();
      loadExpertData();
    } catch (err) {
      toast.error(err.message || 'Payout request failed');
    } finally {
      setRequestingPayout(false);
    }
  };

  const loadExpertData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading expert data for user:', user?.id);

      // Load expert profile
      const { data: profile, error: profileError } = await supabase
        ?.from('expert_profiles')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();

      console.log('📋 Expert profile query result:', { profile, profileError });

      if (profileError && profileError?.code !== 'PGRST116') {
        throw profileError;
      }

      if (!profile) {
        console.warn('⚠️ No expert profile found for user:', user?.id);
        setLoading(false);
        return;
      }

      console.log('✅ Expert profile found:', profile);
      setExpertProfile(profile);

      // Auto-create Stripe Connect account if one doesn't exist yet.
      // Experts can receive destination charges before completing KYC —
      // they just need a Connect account ID. We create it silently here so
      // clients can fund contracts immediately after an expert signs up.
      if (!profile.stripe_connect_account_id) {
        createConnectAccount(profile.id, user?.email, profile.full_name)
          .then((res) => {
            if (res?.success) {
              setExpertProfile((prev) => ({
                ...prev,
                stripe_connect_account_id: res.accountId,
              }));
            }
          })
          .catch((e) => console.warn('Auto Connect account creation failed:', e));
      }

      // Load payouts (earnings) for this expert profile
      const { data: payoutsData } = await supabase
        ?.from('payouts')
        ?.select(`
          *,
          contract:marketplace_contracts(contract_number, title)
        `)
        ?.eq('expert_id', profile.id)
        ?.order('created_at', { ascending: false });
      setPayouts(payoutsData || []);

      // Load average rating from expert_reviews (live, not relying on trigger-maintained field)
      const { data: reviewsData } = await supabase
        ?.from('expert_reviews')
        ?.select('rating')
        ?.eq('expert_id', profile.id);
      if (reviewsData && reviewsData.length > 0) {
        const avg = reviewsData.reduce((sum, r) => sum + (r.rating || 0), 0) / reviewsData.length;
        setAverageRating(avg.toFixed(1));
      } else {
        setAverageRating(null);
      }

      // Load tips received by this expert
      const { data: tipsData } = await supabase
        ?.from('tips')
        ?.select(`
          *,
          contract:marketplace_contracts(contract_number, title)
        `)
        ?.eq('expert_id', profile.id)
        ?.eq('status', 'succeeded')
        ?.order('created_at', { ascending: false });
      setTips(tipsData || []);

      // Load contracts if expert profile exists
      console.log('🔍 Loading contracts for expert profile:', profile.id);
      const contractsResult = await getUserContracts(user?.id, 'expert');
      console.log('📋 Contracts query result:', contractsResult);
      
      if (contractsResult?.success) {
        console.log('✅ Contracts loaded:', contractsResult.contracts?.length, 'contracts');
        setContracts(contractsResult?.contracts || []);
        
        // Extract unique cases from contracts
        const uniqueCases = [];
        const caseIds = new Set();
        
        contractsResult.contracts?.forEach(contract => {
          console.log('🔍 Processing contract:', contract.id, 'case_id:', contract.case_id);
          if (contract.case && contract.case_id && !caseIds.has(contract.case_id)) {
            caseIds.add(contract.case_id);
            uniqueCases.push({
              id: contract.case_id,
              case_number: contract.case.case_number,
              property_address: contract.case.property_address,
              allegation_type: contract.case.allegation_type,
              status: contract.case.status,
              contract_id: contract.id,
              contract_title: contract.title
            });
          }
        });
        
        console.log('✅ Cases extracted:', uniqueCases.length, 'unique cases');
        setCases(uniqueCases);
      } else {
        console.error('❌ Failed to load contracts:', contractsResult?.error);
      }
    } catch (error) {
      console.error('❌ Load expert data error:', error);
      toast?.error('Failed to load dashboard data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConnectAccount = async () => {
    try {
      setConnectOnboarding(true);

      // Create Connect account
      const result = await createConnectAccount(
        expertProfile?.id,
        user?.email,
        expertProfile?.full_name
      );

      if (!result?.success) {
        throw new Error(result?.error);
      }

      // Create onboarding link
      const linkResult = await createAccountLink(result?.accountId, expertProfile?.id);

      if (!linkResult?.success) {
        throw new Error(linkResult?.error);
      }

      // Redirect to Stripe onboarding
      window.location.href = linkResult?.url;
    } catch (error) {
      console.error('Create Connect account error:', error);
      toast?.error(error?.message || 'Failed to start onboarding');
      setConnectOnboarding(false);
    }
  };

  const handleRefreshConnectStatus = async () => {
    try {
      const result = await syncConnectAccountStatus(expertProfile?.id);

      if (!result?.success) {
        throw new Error(result?.error);
      }

      toast?.success('Connect account status updated');
      await loadExpertData();
    } catch (error) {
      console.error('Refresh Connect status error:', error);
      toast?.error(error?.message || 'Failed to refresh status');
    }
  };

  const stats = [
    {
      label: 'Total Contracts',
      value: contracts?.length || 0,
      icon: 'FileText',
      color: 'bg-primary'
    },
    {
      label: 'Total Earnings',
      value: `$${(connectBalance.totalGross ?? 0).toFixed(2)}`,
      icon: 'DollarSign',
      color: 'bg-success'
    },
    {
      label: 'Contracts Completed',
      value: contracts?.filter((c) => c?.status === 'completed')?.length || 0,
      icon: 'CheckCircle',
      color: 'bg-secondary'
    },
    {
      label: 'Average Rating',
      value: averageRating ?? 'N/A',
      icon: 'Star',
      color: 'bg-warning'
    }
  ];



  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!expertProfile) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar userRole="expert" />
        <div className="lg:ml-60 min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center">
            <Icon name="AlertCircle" className="w-16 h-16 text-warning mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Expert Profile Not Found</h2>
            <p className="text-muted-foreground mb-6">
              You need to apply as an expert and get approved before accessing the expert dashboard.
            </p>
            <Button href="/expert-marketplace-listing" iconName="ArrowRight">
              Apply as Expert
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Expert Dashboard - FireCaseIQ</title>
      </Helmet>
      <Sidebar userRole="expert" />
      <QuickActionMenu userRole="expert" />
      {detailContract && (
        <ContractDetailModal
          contract={detailContract}
          expertId={expertProfile?.id}
          onClose={() => setDetailContract(null)}
          onRefresh={loadExpertData}
        />
      )}
      <div className="lg:ml-60 min-h-screen">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-secondary text-white p-6 md:p-8">
          <div className="max-w-7xl mx-auto flex items-start justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  const isCollapsed = document.body.classList.toggle('sidebar-collapsed');
                  localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Toggle sidebar"
              >
                <Icon name="PanelLeft" size={24} />
              </button>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">Expert Dashboard</h1>
                <p className="text-white/80">Manage your contracts, time tracking, and earnings</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="[&>div>button]:text-white [&>div>button:hover]:bg-white/10">
                <NotificationCenter userRole="expert" />
              </div>
              <button
                onClick={async () => { await signOut(); navigate('/login'); }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:text-white border border-white/30 rounded-lg hover:bg-white/10 transition-colors"
              >
                <Icon name="LogOut" size={16} />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Stripe Connect Status */}
        {!expertProfile?.connect_onboarding_completed && (
          <div className="max-w-7xl mx-auto p-4 md:p-6">
            <div className="bg-warning/10 border border-warning rounded-lg p-4 md:p-6">
              <div className="flex items-start gap-4">
                <Icon name="AlertTriangle" className="w-6 h-6 text-warning flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-2">
                    Action Required: Connect Your Bank Account
                  </h3>
                  <p className="text-muted-foreground mb-1">
                    You must connect a bank account before you can accept contracts or appear in the expert marketplace.
                  </p>
                  <p className="text-muted-foreground mb-4 text-sm">
                    Complete this one-time setup to unlock payouts, start receiving cases, and become visible to clients.
                  </p>
                  <Button
                    onClick={() => {
                      navigate('/expert-dashboard/financial');
                      setFinancialTab('bank-setup');
                    }}
                    iconName="CreditCard"
                  >
                    Connect Bank Account
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {expertProfile?.connect_onboarding_completed && !expertProfile?.connect_payouts_enabled && (
          <div className="max-w-7xl mx-auto p-4 md:p-6">
            <div className="bg-info/10 border border-info rounded-lg p-4 md:p-6">
              <div className="flex items-start gap-4">
                <Icon name="Info" className="w-6 h-6 text-info flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-2">
                    Verification In Progress
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Your Stripe Connect account is being verified. Payouts will be enabled once verification is complete.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshConnectStatus}
                    iconName="RefreshCw"
                  >
                    Refresh Status
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats - Only show in overview */}
        {activeView === 'overview' && (
          <div className="max-w-7xl mx-auto p-4 md:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {stats?.map((stat, index) => (
                <div
                  key={index}
                  className="bg-card border border-border rounded-lg p-4 hover:shadow-elevation-md transition-smooth"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">{stat?.label}</span>
                    <div className={`${stat?.color} p-2 rounded-lg`}>
                      <Icon name={stat?.icon} className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto p-4 md:p-6">
          {/* Content Area */}
          <div className="bg-card border border-border rounded-lg overflow-hidden p-4 md:p-6">
              {activeView === 'overview' && (
                <div className="text-center py-12">
                  <Icon name="LayoutDashboard" className="w-16 h-16 text-primary mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-foreground mb-2">Welcome to Your Expert Dashboard</h3>
                  <p className="text-muted-foreground mb-6">
                    Use the sidebar to navigate to different sections of your dashboard.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto mt-8">
                    <button
                      onClick={() => navigate('/expert-dashboard/contracts')}
                      className="p-6 bg-background border border-border rounded-lg hover:border-primary transition-colors text-left"
                    >
                      <Icon name="FileText" className="w-8 h-8 text-primary mb-3" />
                      <h4 className="font-semibold text-foreground mb-1">Contracts</h4>
                      <p className="text-sm text-muted-foreground">View and manage your contracts</p>
                    </button>
                    {/* <button
                      onClick={() => navigate('/expert-dashboard/cases')}
                      className="p-6 bg-background border border-border rounded-lg hover:border-primary transition-colors text-left"
                    >
                      <Icon name="Briefcase" className="w-8 h-8 text-primary mb-3" />
                      <h4 className="font-semibold text-foreground mb-1">My Cases</h4>
                      <p className="text-sm text-muted-foreground">Access your assigned cases</p>
                    </button> */}
                    {/* <button
                      onClick={() => navigate('/expert-dashboard/time-tracking')}
                      className="p-6 bg-background border border-border rounded-lg hover:border-primary transition-colors text-left"
                    >
                      <Icon name="Clock" className="w-8 h-8 text-primary mb-3" />
                      <h4 className="font-semibold text-foreground mb-1">Time Tracking</h4>
                      <p className="text-sm text-muted-foreground">Log your billable hours</p>
                    </button> */}
                    <button
                      onClick={() => navigate('/expert-dashboard/messages')}
                      className="p-6 bg-background border border-border rounded-lg hover:border-primary transition-colors text-left"
                    >
                      <Icon name="MessageSquare" className="w-8 h-8 text-primary mb-3" />
                      <h4 className="font-semibold text-foreground mb-1">Messages</h4>
                      <p className="text-sm text-muted-foreground">Communicate with clients</p>
                    </button>
                    <button
                      onClick={() => navigate('/expert-dashboard/financial')}
                      className="p-6 bg-background border border-border rounded-lg hover:border-primary transition-colors text-left"
                    >
                      <Icon name="DollarSign" className="w-8 h-8 text-primary mb-3" />
                      <h4 className="font-semibold text-foreground mb-1">Financial</h4>
                      <p className="text-sm text-muted-foreground">View your earnings history</p>
                    </button>
                    <button
                      onClick={() => navigate('/expert-marketplace-listing')}
                      className="p-6 bg-background border border-border rounded-lg hover:border-primary transition-colors text-left"
                    >
                      <Icon name="Store" className="w-8 h-8 text-primary mb-3" />
                      <h4 className="font-semibold text-foreground mb-1">Marketplace</h4>
                      <p className="text-sm text-muted-foreground">Browse available cases</p>
                    </button>
                  </div>
                </div>
              )}

              {activeView === 'contracts' && (() => {
                const CONTRACT_TABS = [
                  { id: 'all',         label: 'All',           statuses: null },
                  { id: 'offered',     label: 'New Offers',    statuses: ['offered'] },
                  { id: 'active',      label: 'Active',        statuses: ['accepted', 'in_progress', 'pending_review'] },
                  { id: 'completed',   label: 'Completed',     statuses: ['completed'] },
                  { id: 'closed',      label: 'Closed',        statuses: ['cancelled', 'disputed'] },
                ];
                const counts = CONTRACT_TABS.reduce((acc, tab) => {
                  acc[tab.id] = tab.statuses
                    ? contracts.filter(c => tab.statuses.includes(c.status)).length
                    : contracts.length;
                  return acc;
                }, {});
                const visibleContracts = CONTRACT_TABS.find(t => t.id === contractTab)?.statuses
                  ? contracts.filter(c => CONTRACT_TABS.find(t => t.id === contractTab).statuses.includes(c.status))
                  : contracts;
                return (
                  <div className="space-y-4">
                    {/* Tab bar */}
                    <div className="flex gap-1 bg-muted p-1 rounded-xl overflow-x-auto scrollbar-none">
                      {CONTRACT_TABS.map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => setContractTab(tab.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                            contractTab === tab.id
                              ? 'bg-card text-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {tab.label}
                          {counts[tab.id] > 0 && (
                            <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                              contractTab === tab.id ? 'bg-primary/10 text-primary' : 'bg-border text-muted-foreground'
                            }`}>
                              {counts[tab.id]}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Contract list */}
                    {visibleContracts.length === 0 ? (
                      <div className="text-center py-12">
                        <Icon name="FileText" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                          {contracts.length === 0 ? 'No Contracts Yet' : 'No Contracts Here'}
                        </h3>
                        <p className="text-muted-foreground">
                          {contracts.length === 0
                            ? "You'll see your contracts here once clients send you offers."
                            : 'No contracts match this filter.'}
                        </p>
                      </div>
                    ) : (
                      visibleContracts.map((contract) => (
                        <ContractCard
                          key={contract?.id}
                          contract={contract}
                          onViewDetails={() => setDetailContract(contract)}
                          onMessages={() => {
                            setSelectedContract(contract);
                            navigate('/expert-dashboard/messages');
                          }}
                          onRefresh={loadExpertData}
                        />
                      ))
                    )}
                  </div>
                );
              })()}

              {activeView === 'cases' && (
                <div className="space-y-4">
                  {cases?.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon name="Briefcase" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Cases Assigned Yet</h3>
                      <p className="text-muted-foreground">
                        Cases you're assigned to will appear here.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {cases?.map((caseItem) => (
                        <div
                          key={caseItem?.id}
                          className="bg-background border border-border rounded-lg p-4 hover:border-primary transition-colors cursor-pointer"
                          onClick={() => navigate(`/report-viewer/${caseItem?.id}`)}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h4 className="font-semibold text-foreground mb-1">
                                Case #{caseItem?.case_number}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {caseItem?.property_address || 'No address provided'}
                              </p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              caseItem?.status === 'completed' ? 'bg-success/10 text-success' :
                              caseItem?.status === 'in_progress' ? 'bg-primary/10 text-primary' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {caseItem?.status?.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Icon name="Calendar" className="w-4 h-4" />
                              {new Date(caseItem?.created_at)?.toLocaleDateString()}
                            </div>
                            {caseItem?.property_type && (
                              <div className="flex items-center gap-1">
                                <Icon name="Home" className="w-4 h-4" />
                                {caseItem?.property_type}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeView === 'time-tracking' && (
                <TimeEntryForm
                  contracts={contracts?.filter((c) => c?.contract_type === 'hourly' && c?.status === 'in_progress')}
                  expertId={expertProfile?.id}
                  onSuccess={loadExpertData}
                />
              )}

              {activeView === 'messages' && (
                <MessagingPanel
                  contracts={contracts}
                  userId={user?.id}
                  userType="expert"
                  initialUserId={msgInitUserId}
                />
              )}

              {activeView === 'financial' && (() => {
                // Gross Earned = directly from Stripe balance transactions (most accurate)
                const totalGross = connectBalance.totalGross
                                 + tips.reduce((s, t) => s + (t.amount || 0), 0);
                // Released contracts = for the earnings ledger table rows
                const releasedContracts = contracts.filter(c => c.escrow_status === 'released');
                // Bank withdrawals: paid/processing payouts
                const totalPaidOut = payouts.filter(p => ['paid','processing'].includes(p.status))
                                            .reduce((s, p) => s + (p.net_amount || 0), 0);
                // Held in escrow = API-provided (DB-authoritative, not conflated with Stripe pending)
                const totalHeldAmt = connectBalance.heldInEscrow;
                const bankSetupDone   = expertProfile?.connect_onboarding_completed && expertProfile?.connect_payouts_enabled && !!expertProfile?.stripe_connect_account_id;
                const bankPending     = expertProfile?.connect_onboarding_completed && !expertProfile?.connect_payouts_enabled;

                return (
                  <div>
                    {/* Inner tab bar */}
                    <div className="flex items-center gap-1 border-b border-border mb-6 -mx-4 md:-mx-6 px-4 md:px-6">
                      {[
                        { id: 'overview',     label: 'Overview',     icon: 'BarChart2' },
                        { id: 'earnings',     label: 'Earnings',     icon: 'TrendingUp' },
                        { id: 'payouts',      label: 'Bank Payouts', icon: 'ArrowDownToLine' },
                        { id: 'bank-setup',   label: 'Bank Account', icon: 'Landmark' },
                      ].map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => setFinancialTab(tab.id)}
                          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                            financialTab === tab.id
                              ? 'border-primary text-primary'
                              : 'border-transparent text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <Icon name={tab.icon} size={15} />
                          {tab.label}
                          {tab.id === 'bank-setup' && !bankSetupDone && (
                            <span className="ml-0.5 w-2 h-2 rounded-full bg-warning flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>

                    {/* ── Overview ── */}
                    {financialTab === 'overview' && (
                      <div className="space-y-6">
                        {/* Connect balance — shows available + pending from Stripe */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 bg-primary/5 border border-primary/20 rounded-2xl">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Icon name="Banknote" size={22} className="text-primary" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Available Balance</p>
                              <p className="text-3xl font-bold text-foreground">
                                {balanceLoaded ? `$${connectBalance.available.toFixed(2)}` : '—'}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {bankSetupDone
                                  ? 'Ready to transfer to your bank'
                                  : 'Complete KYC to request payouts'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={async () => {
                                setBalanceLoaded(false);
                                await refreshBalance();
                              }}
                              disabled={!balanceLoaded}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground disabled:opacity-50 disabled:pointer-events-none"
                              title="Refresh balance from Stripe"
                            >
                              <Icon name={!balanceLoaded ? 'Loader2' : 'RefreshCw'} size={13} className={!balanceLoaded ? 'animate-spin' : ''} />
                              {!balanceLoaded ? 'Loading...' : 'Refresh'}
                            </button>
                            {bankSetupDone ? (
                              <Button
                                onClick={() => { setPayoutAmount((connectBalance.available + connectBalance.pending).toFixed(2)); setShowPayoutModal(true); }}
                                disabled={connectBalance.available + connectBalance.pending <= 0}
                                iconName="ArrowDownToLine"
                                iconPosition="left"
                              >
                                Request Payout
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                onClick={() => setFinancialTab('bank-setup')}
                                iconName="Landmark"
                                iconPosition="left"
                              >
                                Set Up Bank
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Held in escrow — client paid but work not approved yet */}
                        {totalHeldAmt > 0 && (
                          <div className="flex items-center gap-3 p-4 bg-muted/40 border border-border rounded-xl">
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                              <Icon name="Lock" size={16} className="text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground">
                                ${totalHeldAmt.toFixed(2)} held in escrow
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Payment locked — released to you once the client approves your completed work.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Settling — escrow released, clearing Stripe's 2-day window */}
                        {connectBalance.pending > 0 && (
                          <div className="flex items-center gap-3 p-4 bg-warning/5 border border-warning/30 rounded-xl">
                            <div className="w-8 h-8 rounded-full bg-warning/10 flex items-center justify-center flex-shrink-0">
                              <Icon name="Clock" size={16} className="text-warning" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground">
                                ${connectBalance.pending.toFixed(2)} settling
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Escrow was released — funds are clearing through Stripe and will be available to pay out in 1–2 business days.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Stats cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          {[
                            { label: 'Available',      value: `$${connectBalance.available.toFixed(2)}`, icon: 'Banknote',   colorText: 'text-success',          colorBg: 'bg-success/10', title: 'Ready to pay out to your bank' },
                            { label: 'Settling',       value: `$${connectBalance.pending.toFixed(2)}`,   icon: 'Clock',      colorText: 'text-warning',          colorBg: 'bg-warning/10', title: 'Released escrow clearing through Stripe (1–2 days)' },
                            { label: 'Held in Escrow', value: `$${totalHeldAmt.toFixed(2)}`,             icon: 'Lock',       colorText: 'text-muted-foreground', colorBg: 'bg-muted',      title: 'Locked — released when client approves your work' },
                            { label: 'Gross Earned',   value: `$${totalGross.toFixed(2)}`,               icon: 'TrendingUp', colorText: 'text-primary',          colorBg: 'bg-primary/10', title: 'Total earned from released contracts' },
                          ].map((card) => (
                            <div key={card.label} className="bg-background border border-border rounded-xl p-5" title={card.title}>
                              <div className="flex items-center justify-between mb-3">
                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{card.label}</p>
                                <div className={`${card.colorBg} w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0`}>
                                  <Icon name={card.icon} size={16} className={card.colorText} />
                                </div>
                              </div>
                              <p className="text-2xl font-bold text-foreground">{card.value}</p>
                            </div>
                          ))}
                        </div>

                        {(releasedContracts.length > 0 || tips.length > 0) ? (
                          <div className="bg-background border border-border rounded-xl overflow-hidden">
                            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                              <h4 className="font-semibold text-foreground">Recent Activity</h4>
                              <button onClick={() => setFinancialTab('earnings')} className="text-xs text-primary hover:underline font-medium">
                                View all →
                              </button>
                            </div>
                            <div className="divide-y divide-border">
                              {[
                                ...releasedContracts.map(c => ({ ...c, _type: 'contract' })),
                                ...tips.map(t => ({ ...t, _type: 'tip' }))
                              ]
                                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                .slice(0, 5)
                                .map((item) => {
                                  const amount = item._type === 'tip'
                                    ? (item.net_amount ?? item.amount)
                                    : (() => {
                                        // Use total_contract_value (set to approvedAmount on release)
                                        // not escrow_amount (the original locked amount).
                                        const gross = item.total_contract_value || item.escrow_amount || 0;
                                        const commPct = item.commission_pct ?? 10;
                                        return parseFloat((gross * (1 - commPct / 100)).toFixed(2));
                                      })();
                                  return (
                                <div key={item._type + item.id} className="flex items-center justify-between px-5 py-3 gap-4">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                      item._type === 'tip' ? 'bg-amber-500/10' : 'bg-success/10'
                                    }`}>
                                      <Icon
                                        name={item._type === 'tip' ? 'Heart' : 'Banknote'}
                                        size={15}
                                        className={item._type === 'tip' ? 'text-amber-500' : 'text-success'}
                                      />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-foreground truncate">
                                        {item._type === 'tip' ? `Tip — ${item.contract?.title || 'Contract'}` : (item.title || item.contract_number || 'Contract')}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                      </p>
                                    </div>
                                  </div>
                                  <p className="text-sm font-bold text-foreground flex-shrink-0">
                                    ${amount?.toFixed(2)}
                                  </p>
                                </div>
                              ); })}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <Icon name="DollarSign" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-foreground mb-2">No Earnings Yet</h3>
                            <p className="text-muted-foreground">Complete contracts to start earning.</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Earnings (incoming ledger — released contracts + tips) ── */}
                    {financialTab === 'earnings' && (() => {
                      const combined = [
                        ...releasedContracts.map(c => ({ ...c, _type: 'contract' })),
                        ...tips.map(t => ({ ...t, _type: 'tip' }))
                      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                      return (
                      <div>
                        {combined.length === 0 ? (
                          <div className="text-center py-12">
                            <Icon name="TrendingUp" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-foreground mb-2">No Earnings Yet</h3>
                            <p className="text-muted-foreground">Your earnings will appear here once contracts are completed.</p>
                          </div>
                        ) : (
                          <div className="bg-background border border-border rounded-xl overflow-hidden">
                            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                              <h4 className="font-semibold text-foreground">Earnings Ledger</h4>
                              <span className="text-xs text-muted-foreground">{combined.length} record{combined.length !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-muted/50 border-b border-border">
                                  <tr>
                                    <th className="text-left px-5 py-3 font-medium text-muted-foreground">Date</th>
                                    <th className="text-left px-5 py-3 font-medium text-muted-foreground">Source</th>
                                    <th className="text-right px-5 py-3 font-medium text-muted-foreground">Gross</th>
                                    <th className="text-right px-5 py-3 font-medium text-muted-foreground">Commission</th>
                                    <th className="text-right px-5 py-3 font-medium text-muted-foreground">Net</th>
                                    <th className="text-center px-5 py-3 font-medium text-muted-foreground">Status</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                  {combined.map((item) => {
                                    // Use total_contract_value (set to approvedAmount on release),
                                    // not escrow_amount (the original locked amount before partial refund).
                                    const gross = item._type === 'tip' ? item.amount : (item.total_contract_value || item.escrow_amount || 0);
                                    const commPct = item.commission_pct ?? 10;
                                    const commission = item._type === 'tip' ? (item.commission_amount || 0) : parseFloat((gross * commPct / 100).toFixed(2));
                                    const net = item._type === 'tip' ? (item.net_amount ?? item.amount) : parseFloat((gross - commission).toFixed(2));
                                    return (
                                    <tr key={item._type + item.id} className="hover:bg-muted/40 transition-colors">
                                      <td className="px-5 py-3 text-muted-foreground whitespace-nowrap">
                                        {new Date(item.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                      </td>
                                      <td className="px-5 py-3">
                                        <div className="flex items-center gap-2">
                                          {item._type === 'tip' && (
                                            <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-amber-500/10 text-amber-600">
                                              <Icon name="Heart" size={10} /> Tip
                                            </span>
                                          )}
                                          <div className="min-w-0">
                                            <p className="font-medium text-foreground truncate max-w-[180px]">
                                              {item._type === 'tip' ? (item.contract?.title || 'Tip') : (item.title || item.contract_number || '—')}
                                            </p>
                                            {item.contract_number && item._type === 'contract' && (
                                              <p className="text-xs text-muted-foreground">{item.contract_number}</p>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-5 py-3 text-right text-foreground">${gross.toFixed(2)}</td>
                                      <td className="px-5 py-3 text-right text-destructive">−${commission.toFixed(2)}</td>
                                      <td className="px-5 py-3 text-right font-bold text-foreground">${net.toFixed(2)}</td>
                                      <td className="px-5 py-3 text-center">
                                        {item._type === 'tip' ? (
                                          <span className="inline-block text-xs px-2.5 py-0.5 rounded-full font-medium bg-amber-500/10 text-amber-600">Received</span>
                                        ) : (
                                          <span className={`inline-block text-xs px-2.5 py-0.5 rounded-full font-medium ${
                                            item.payout_status === 'paid'     ? 'bg-success/10 text-success' :
                                            item.payout_status === 'released' ? 'bg-primary/10 text-primary' :
                                            'bg-muted text-muted-foreground'
                                          }`}>
                                            {item.payout_status === 'paid' ? 'Withdrawn' : 'In Balance'}
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })()}

                    {/* ── Bank Payouts (outgoing to bank) ── */}
                    {financialTab === 'payouts' && (() => {
                      const bankPayouts = payouts.filter(p => ['paid', 'processing', 'failed'].includes(p.status));
                      return (
                      <div>
                        {bankPayouts.length === 0 ? (
                          <div className="text-center py-12">
                            <Icon name="ArrowDownToLine" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-foreground mb-2">No Bank Payouts Yet</h3>
                            <p className="text-muted-foreground">When you request payouts to your bank, they'll appear here.</p>
                          </div>
                        ) : (
                          <div className="bg-background border border-border rounded-xl overflow-hidden">
                            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                              <h4 className="font-semibold text-foreground">Bank Payouts</h4>
                              <span className="text-xs text-muted-foreground">{bankPayouts.length} record{bankPayouts.length !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-muted/50 border-b border-border">
                                  <tr>
                                    <th className="text-left px-5 py-3 font-medium text-muted-foreground">Date</th>
                                    <th className="text-left px-5 py-3 font-medium text-muted-foreground">Contract</th>
                                    <th className="text-right px-5 py-3 font-medium text-muted-foreground">Amount</th>
                                    <th className="text-center px-5 py-3 font-medium text-muted-foreground">Status</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                  {bankPayouts.map((p) => (
                                    <tr key={p.id} className="hover:bg-muted/40 transition-colors">
                                      <td className="px-5 py-3 text-muted-foreground whitespace-nowrap">
                                        {new Date(p.updated_at || p.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                      </td>
                                      <td className="px-5 py-3 text-foreground truncate max-w-[200px]">{p.contract?.title || '—'}</td>
                                      <td className="px-5 py-3 text-right font-bold text-foreground">${(p.net_amount || 0).toFixed(2)}</td>
                                      <td className="px-5 py-3 text-center">
                                        <span className={`inline-block text-xs px-2.5 py-0.5 rounded-full font-medium ${
                                          p.status === 'paid'       ? 'bg-success/10 text-success' :
                                          p.status === 'processing' ? 'bg-primary/10 text-primary' :
                                          p.status === 'failed'     ? 'bg-destructive/10 text-destructive' :
                                          'bg-muted text-muted-foreground'
                                        }`}>
                                          {p.status === 'processing' ? 'In Transit' : p.status === 'paid' ? 'Completed' : p.status}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })()}

                    {/* ── Bank Account ── */}
                    {financialTab === 'bank-setup' && (
                      <div className="space-y-6 max-w-2xl">
                        <div>
                          <h3 className="text-base font-semibold text-foreground mb-1">Bank Account &amp; Payouts</h3>
                          <p className="text-sm text-muted-foreground">
                            Connect your bank account through Stripe to receive payouts when contracts are completed.
                            A platform commission of {expertProfile?.commission_pct ?? 10}% is deducted from each payout.
                          </p>
                        </div>

                        {/* Status card */}
                        <div className={`rounded-xl border p-5 ${
                          bankSetupDone ? 'border-success/40 bg-success/5' :
                          bankPending   ? 'border-warning/40 bg-warning/5'  :
                          'border-border bg-background'
                        }`}>
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              bankSetupDone ? 'bg-success/15' : bankPending ? 'bg-warning/15' : 'bg-muted'
                            }`}>
                              <Icon
                                name={bankSetupDone ? 'CheckCircle' : bankPending ? 'Clock' : 'Landmark'}
                                size={20}
                                className={bankSetupDone ? 'text-success' : bankPending ? 'text-warning' : 'text-muted-foreground'}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-foreground">
                                {bankSetupDone ? 'Bank account connected' : bankPending ? 'Verification in progress' : 'No bank account connected'}
                              </p>
                              <p className="text-sm text-muted-foreground mt-0.5">
                                {bankSetupDone
                                  ? 'Payouts are enabled. Funds will be transferred to your bank after each completed contract.'
                                  : bankPending
                                  ? 'Stripe is verifying your account details. This usually takes 1–2 business days.'
                                  : 'You must connect a bank account before you can receive payouts from completed contracts.'}
                              </p>



                              {bankSetupDone && (
                                <div className="mt-4 flex flex-wrap gap-3">
                                  {[
                                    { label: 'Charges enabled', ok: expertProfile?.connect_charges_enabled },
                                    { label: 'Payouts enabled',  ok: expertProfile?.connect_payouts_enabled },
                                  ].map((item) => (
                                    <div key={item.label} className="flex items-center gap-1.5 text-sm">
                                      <Icon name={item.ok ? 'CheckCircle' : 'XCircle'} size={15} className={item.ok ? 'text-success' : 'text-destructive'} />
                                      <span className={item.ok ? 'text-foreground' : 'text-muted-foreground'}>{item.label}</span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <div className="flex flex-wrap items-center gap-3 mt-4">
                                {!expertProfile?.connect_onboarding_completed && (
                                  <Button
                                    onClick={handleCreateConnectAccount}
                                    disabled={connectOnboarding}
                                    iconName={connectOnboarding ? 'Loader' : 'ExternalLink'}
                                    iconPosition="left"
                                  >
                                    {connectOnboarding ? 'Redirecting…' : 'Connect Bank Account'}
                                  </Button>
                                )}
                                {(bankPending || bankSetupDone) && (
                                  <Button
                                    variant="outline"
                                    onClick={handleRefreshConnectStatus}
                                    iconName="RefreshCw"
                                    iconPosition="left"
                                  >
                                    {bankSetupDone ? 'Sync Status' : 'Refresh Status'}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* How payouts work */}
                        <div className="bg-muted/40 border border-border rounded-xl p-5">
                          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Icon name="Info" size={15} className="text-primary" />
                            How payouts work
                          </h4>
                          <ul className="space-y-2 text-sm text-muted-foreground">
                            {[
                              'When a client pays for a contract, the funds go directly to your Stripe Connect balance (minus platform commission).',
                              `The platform takes a ${expertProfile?.commission_pct ?? 10}% commission — the net amount sits in your Stripe balance.`,
                              'During active contracts, payouts are held until the client releases the escrow.',
                              'Once released, you can request a bank payout from the Overview tab.',
                              'Bank Transfer (ACH) — free, arrives in 2–3 business days.',
                              'Instant Payout (debit card) — ~30 minutes, 1% fee charged by Stripe.',
                              'You can start earning before adding bank details — just complete KYC when you want to cash out.',
                            ].map((line, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <Icon name="ChevronRight" size={14} className="text-primary flex-shrink-0 mt-0.5" />
                                {line}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
          </div>
        </div>
      </div>

      {/* ── Payout Modal ── */}
      {showPayoutModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowPayoutModal(false)} />
          <div className="relative z-10 bg-card w-full max-w-md mx-4 rounded-2xl border border-border shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <h2 className="text-base font-bold text-foreground">Request Payout</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Available: <span className="font-semibold text-foreground">${connectBalance.available.toFixed(2)}</span>
                  {connectBalance.pending > 0 && (
                    <span> · Settling: <span className="font-semibold text-warning">${connectBalance.pending.toFixed(2)}</span> <span className="text-muted-foreground">(will be fast-forwarded in test mode)</span></span>
                  )}
                </p>
              </div>
              <button onClick={() => setShowPayoutModal(false)} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground">
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5">
              {/* Amount */}
              <div>
                <label className="text-sm font-semibold text-foreground mb-1.5 block">Amount ($)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">$</span>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    max={connectBalance.available + connectBalance.pending}
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                    className="w-full pl-7 pr-3 py-2.5 text-sm bg-background border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <button
                  onClick={() => setPayoutAmount((connectBalance.available + connectBalance.pending).toFixed(2))}
                  className="text-xs text-primary hover:underline mt-1"
                >
                  Payout all (${(connectBalance.available + connectBalance.pending).toFixed(2)})
                </button>
              </div>

              {/* Method */}
              <div>
                <label className="text-sm font-semibold text-foreground mb-2 block">Payout Method</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'standard', icon: 'Landmark', label: 'Bank Transfer',  sub: '2–3 business days · Free' },
                    { id: 'instant',  icon: 'Zap',      label: 'Instant Payout', sub: '~30 minutes · 1% fee' },
                  ].map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setPayoutMethod(m.id)}
                      className={`flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                        payoutMethod === m.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40 bg-background'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${payoutMethod === m.id ? 'bg-primary' : 'bg-muted'}`}>
                        <Icon name={m.icon} size={15} color={payoutMethod === m.id ? '#fff' : undefined} className={payoutMethod === m.id ? '' : 'text-muted-foreground'} />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-foreground">{m.label}</p>
                        <p className="text-[11px] text-muted-foreground">{m.sub}</p>
                      </div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Funds go to the bank account or debit card you set up during Stripe onboarding.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 px-6 py-4 border-t border-border bg-muted/20">
              <Button variant="outline" className="w-24" onClick={() => setShowPayoutModal(false)} disabled={requestingPayout}>
                Cancel
              </Button>
              <Button
                variant="default"
                className="flex-1"
                disabled={requestingPayout || !payoutAmount || parseFloat(payoutAmount) <= 0}
                iconName={requestingPayout ? 'Loader' : 'ArrowDownToLine'}
                iconPosition="left"
                onClick={handleRequestPayout}
              >
                {requestingPayout ? 'Processing…' : `Payout $${parseFloat(payoutAmount || 0).toFixed(2)}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpertDashboard;